import { Link } from "@tanstack/react-router";
import { ChevronRight, CalendarClock, Crown } from "lucide-react";

import { cn } from "@/lib/utils";
import type { QuotaStatus, UserSubscription } from "@/lib/boide-api";

function formatMoney(amount?: number | null) {
  return `${Math.max(0, Math.round(Number(amount) || 0)).toLocaleString("vi-VN")}đ`;
}

/**
 * Tên gói để hiện trên một dòng.
 *
 * ⚠️ `subscriptions` là MẢNG và các đoạn ĐƯỢC PHÉP chồng nhau (quota ngày là
 * TỔNG mức của mọi đoạn phủ ngày đó). Lấy `subscriptions[0]` rồi gọi đó là "gói
 * của tôi" là nói sai với người đang có hai gói chạy song song — nên gói mức cao
 * nhất đứng tên, phần còn lại hiện thành "+N".
 */
export function planLabelOf(subscriptions: UserSubscription[]): {
  primary: UserSubscription | null;
  extra: number;
} {
  const live = (subscriptions || []).filter((sub) => sub.is_live !== false);
  if (!live.length) return { primary: null, extra: 0 };
  const primary = live.reduce((best, sub) => (sub.daily_quota > best.daily_quota ? sub : best));
  return { primary, extra: live.length - 1 };
}

/**
 * Dòng quota nằm NGAY DƯỚI số dư ví: tên gói + hạn mức còn lại hôm nay.
 *
 * Vì sao dưới ví chứ không phải một thẻ riêng: quota và ví là MỘT túi tiền khi
 * đi tiêu (`spendable = quota còn lại + ví`), nên tách chúng ra hai chỗ trên màn
 * hình là để người dùng nhìn số dư 0đ rồi kết luận mình hết tiền trong khi vẫn
 * còn 200.000đ quota của gói.
 */
export function QuotaRow({
  quota,
  inline = false,
  className,
}: {
  quota?: QuotaStatus | null;
  /** `true` = biến thể gọn cho thẻ ví trên mobile (chữ nhỏ hơn, padding hẹp hơn). */
  inline?: boolean;
  className?: string;
}) {
  // 🔴 KHÔNG hiện gì khi chưa có dữ liệu (đang tải, mất mạng, hoặc BE chưa deploy
  // `/v1/subscriptions/*`): hiện lời mời "Mua gói" lúc ấy là nói với người ĐANG CÓ
  // gói rằng họ không có, và trong ca BE chưa lên thì mời họ vào một trang chỉ có
  // lỗi. Một dòng vắng mặt thì không ai thấy; một dòng nói sai thì có.
  if (!quota) return null;

  const { primary, extra } = planLabelOf(quota.subscriptions ?? []);
  const limit = Math.max(0, Number(quota.quota_limit) || 0);
  const remaining = Math.max(0, Number(quota.remaining) || 0);
  // Thanh chia cho `quota_limit`, KHÔNG chia cho `daily_quota`: dòng sổ hôm nay
  // có thể giữ trần cao hơn tổng các đoạn hiện tại, và khi ấy thanh vượt 100%.
  const percent = limit > 0 ? Math.max(0, Math.min(100, (remaining / limit) * 100)) : 0;

  // Chưa có gói: một lời mời gọn, không phải một khối trống. Vẫn giữ đúng chỗ
  // "dưới ví" để người dùng biết chỗ tìm về sau.
  if (!quota.has_subscription || !primary) {
    return (
      <Link
        to="/goi"
        className={cn(
          "group flex min-w-0 items-center gap-2 border-t border-border/35 transition hover:bg-primary/[0.04]",
          inline ? "px-3.5 py-1.5 text-[10px]" : "px-3.5 py-2 text-[11px]",
          className,
        )}
        aria-label="Xem các gói quota ngày"
      >
        <Crown
          className={cn("shrink-0 text-primary/70", inline ? "size-3.5" : "size-4")}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1 truncate text-muted-foreground">
          Chưa có gói · <strong className="font-semibold text-foreground">Mua gói</strong> để có hạn
          mức mỗi ngày
        </span>
        <ChevronRight className="size-3 shrink-0 text-primary transition group-hover:translate-x-0.5" />
      </Link>
    );
  }

  return (
    <Link
      to="/goi"
      className={cn(
        "group block border-t border-border/35 transition hover:bg-primary/[0.04]",
        inline ? "px-3.5 py-1.5" : "px-3.5 py-2",
        className,
      )}
      aria-label={`Gói ${primary.plan_name}: còn ${formatMoney(remaining)} trong hạn mức ${formatMoney(limit)} hôm nay. Xem chi tiết`}
    >
      <div
        className={cn(
          "mb-1 flex min-w-0 items-center gap-2",
          inline ? "text-[9px] leading-3" : "text-[10px] leading-3.5",
        )}
      >
        <Crown
          className={cn("shrink-0 text-primary", inline ? "size-3" : "size-3.5")}
          aria-hidden="true"
        />
        {/* ⚠️ KHÔNG viết "Gói {tên}": tên gói trong DB đã là "Gói Lớn" / "Gói Nhỏ",
            ghép thêm là ra "Gói Gói Lớn" — đã thấy đúng chuỗi đó khi đo trên máy.
            Biểu tượng vương miện bên trái đã đủ để phân biệt với dòng số dư. */}
        <span className="min-w-0 flex-1 truncate font-medium text-muted-foreground">
          <strong className="font-bold text-foreground">{primary.plan_name}</strong>
          {extra > 0 ? <span className="text-primary"> +{extra}</span> : null}
        </span>
        <span className="shrink-0 tabular-nums text-muted-foreground">
          <strong className="font-bold text-primary">{formatMoney(remaining)}</strong>
          {" / "}
          {formatMoney(limit)}
        </span>
        <ChevronRight className="size-3 shrink-0 text-primary transition group-hover:translate-x-0.5" />
      </div>
      <span className="block h-1 overflow-hidden rounded-full bg-primary/12" aria-hidden="true">
        <span
          className="block h-full rounded-full bg-primary transition-[width]"
          style={{ width: `${percent}%` }}
        />
      </span>
      <span
        className={cn(
          "mt-1 flex min-w-0 items-center gap-1 text-muted-foreground",
          inline ? "text-[8px] leading-3" : "text-[9px] leading-3.5",
        )}
      >
        <CalendarClock className="size-2.5 shrink-0" aria-hidden="true" />
        <span className="truncate">
          Hạn mức làm mới mỗi ngày · còn{" "}
          <strong className="font-semibold text-foreground">{primary.days_left} ngày</strong>
        </span>
      </span>
    </Link>
  );
}
